import produce from 'immer';
import { EATING_WAY_AREA_IMAGE_TYPE, PATTERN_TYPE } from 'utils';
import { Course, MenuBook } from './models';
import { MenuBookCourseBase } from './models/MenuBookCourseBase';
import { TimeFrame } from './models/TimeFrame';
import { MultiSelectToArg, MutilDragAwareArg, ReorderResult } from './types';

export function reorder(list: Array<any>, startIndex: number, endIndex: number) {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
}

const reorderSingleDrag = ({
  data,
  selectedItemIds,
  source,
  destination,
  type,
  droppableId,
}: MutilDragAwareArg): ReorderResult => {
  const [, menuBookId] = droppableId.split('-');
  const shallow = [...data];
  let reorderData = shallow;

  if (type === 'menubook') {
    reorderData = reorder(shallow, source.index, destination.index);
  }

  if (type === 'course') {
    reorderData = produce(shallow, (draft) => {
      const menuBook = draft.find((menuBook) => menuBook.id === Number(menuBookId));
      if (menuBook && destination) {
        const newCourses = reorder(menuBook.courses, source.index, destination.index);
        menuBook.courses = newCourses;
      }
    });
  }

  return {
    data: reorderData,
    selectedItemIds,
  };
};

function getDataListByType(data: MenuBook[], type: string, droppableId: string): MenuBookCourseBase[] {
  if (type === 'menubook') {
    return data;
  }

  if (type === 'course') {
    const [, menuBookId] = droppableId.split('-');

    const menuBook = data.find((menuBook) => menuBook.id === Number(menuBookId));
    if (menuBook) {
      return menuBook.courses;
    }
  }

  return [];
}

function getDataWithUpdatedItemByType(
  data: MenuBook[],
  type: string,
  updateData: MenuBookCourseBase[],
  droppableId: string
) {
  if (type === 'menubook') {
    return updateData as MenuBook[];
  }

  if (type === 'course') {
    const [, menuBookId] = droppableId.split('-');

    return produce(data, (draft) => {
      const menuBook = draft.find((menuBook) => menuBook.id === Number(menuBookId));
      if (menuBook) {
        menuBook.courses = updateData as Course[];
      }
    });
  }

  return [];
}

const reorderMultiDrag = ({
  data,
  selectedItemIds,
  source,
  destination,
  type,
  droppableId,
}: MutilDragAwareArg): ReorderResult => {
  const sourceList = getDataListByType(data, type, droppableId);
  const dragged = sourceList[source.index].id;

  const insertAtIndex: number = (() => {
    const destinationIndexOffset: number = selectedItemIds.reduce((previous: number, current: number): number => {
      if (current === dragged) {
        return previous;
      }

      const index = sourceList.findIndex((item) => item.id === current);

      if (index >= destination.index) {
        return previous;
      }

      // the selected item is before the destination index
      // we need to account for this when inserting into the new location
      return previous + 1;
    }, 0);

    const result: number = destination.index - destinationIndexOffset;
    return result;
  })();

  // doing the ordering now as we are required to look up columns
  // and know original ordering
  const orderedSelectedItemIds = [...selectedItemIds];

  const remainings = sourceList.filter((item) => !selectedItemIds.includes(item.id));
  const base = [...remainings];
  const orderedSelectedItem = sourceList.filter((item) => selectedItemIds.includes(item.id));
  base.splice(insertAtIndex, 0, ...orderedSelectedItem);

  const updated = getDataWithUpdatedItemByType(data, type, base, droppableId);

  return {
    data: updated,
    selectedItemIds: orderedSelectedItemIds,
  };
};

export const mutliDragAwareReorder = (args: MutilDragAwareArg): ReorderResult => {
  if (args.selectedItemIds.length > 1) {
    return reorderMultiDrag(args);
  }

  return reorderSingleDrag(args);
};

export const multiSelectTo = (
  { data, selectedItemIds, type, droppableId }: MultiSelectToArg,
  newItemId: number
): number[] | null => {
  const sourceList = getDataListByType(data, type, droppableId);

  // Nothing already selected
  if (!selectedItemIds.length) {
    return [newItemId];
  }

  const indexOfNew = sourceList.findIndex((item) => item.id === newItemId);
  const lastSelected = selectedItemIds[selectedItemIds.length - 1];
  const indexOfLast = sourceList.findIndex((item) => item.id === lastSelected);

  // nothing to do here
  if (indexOfNew === indexOfLast) {
    return null;
  }

  const isSelectingForwards: boolean = indexOfNew > indexOfLast;
  const start: number = isSelectingForwards ? indexOfLast : indexOfNew;
  const end: number = isSelectingForwards ? indexOfNew : indexOfLast;

  const inBetweenItems = sourceList.slice(start, end + 1);
  const inBetween = inBetweenItems.map((item) => item.id);

  // everything inbetween needs to have it's selection toggled.
  // with the exception of the start and end values which will always be selected
  const toAdd = inBetween.filter((id: number): boolean => {
    // if already selected: then no need to select it again
    if (selectedItemIds.includes(id)) {
      return false;
    }
    return true;
  });

  const sorted = isSelectingForwards ? toAdd : [...toAdd].reverse();
  const combined = [...selectedItemIds, ...sorted];

  return combined;
};

// Determines if the platform specific toggle selection in group key was used
export function wasToggleInSelectionGroupKeyUsed(
  event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
) {
  const platform = (navigator as any).userAgentData?.platform;
  const isUsingWindows = platform && (platform.indexOf('Win') >= 0 || platform.indexOf('Linux') >= 0);
  return isUsingWindows ? event.ctrlKey : event.metaKey;
}

// Determines if the multiSelect key was used
export function wasMultiSelectKeyUsed(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) {
  return event.shiftKey;
}

export function convertTimeFrameDataToSetArray(
  timeFrames: TimeFrame[],
  propertyStart: string,
  propertyEnd: string,
  includedNullValue: boolean = false
): Array<Array<number | null>> {
  const sets: Array<Array<string | null>> = [];
  timeFrames.forEach((timeFrame) => {
    let start: string | null = (timeFrame as any)[propertyStart];
    let end: string | null = (timeFrame as any)[propertyEnd];
    start = start === null || start.length === 0 ? null : start;
    end = end === null || end.length === 0 ? null : end;
    if (includedNullValue) {
      sets.push([start, end]);
    } else {
      if (start !== null && end !== null) {
        sets.push([start, end]);
      }
    }
  });

  const arrs = sets.map((arr) => {
    return arr.map((item) => {
      let convertValue = includedNullValue ? null : 0;
      if (item === 'Open') {
        convertValue = 0;
      } else if (item === 'Close') {
        convertValue = 2400;
      } else {
        if (item !== null) {
          convertValue = Number(item.replace(':', ''));
        }
      }
      return convertValue;
    });
  });

  return arrs;
}

export function checkTimeFrameIntersection(timeFrames: TimeFrame[], propertyStart: string, propertyEnd: string) {
  const arrs = convertTimeFrameDataToSetArray(timeFrames, propertyStart, propertyEnd) as Array<Array<number>>;
  for (let i = 0; i < arrs.length - 1; i++) {
    for (let j = i + 1; j < arrs.length; j++) {
      const set1 = arrs[i];
      const set2 = arrs[j];
      if (set1[0] < set2[1] && set1[1] > set2[0]) {
        return true; // has intersection
      }
    }
  }
  return false; // has no intersection
}

export function checkValidTimeRange(timeFrames: TimeFrame[], propertyStart: string, propertyEnd: string) {
  const arrs = convertTimeFrameDataToSetArray(timeFrames, propertyStart, propertyEnd, true) as Array<Array<number>>;
  for (let i = 0; i < arrs.length; i++) {
    if (arrs[i][0] === null && arrs[i][1] !== null) {
      return false;
    } else if (arrs[i][0] !== null && arrs[i][1] === null) {
      return false;
    } else {
      if (arrs[i][0] !== null && arrs[i][1] !== null && arrs[i][0] > arrs[i][1]) {
        return false;
      }
    }
  }
  return true;
}

export const getImageMeta = (url: string, cb: (img: HTMLImageElement | null, error: string | null) => void) => {
  const img = new Image();
  img.onload = () => cb(img, null);
  img.onerror = (err) => cb(null, err.toString());
  img.src = url;
};

export function getAreaTypeByPatternAndPosition(pattern: string, position: number) {
  switch (pattern) {
    case PATTERN_TYPE.P1:
      return EATING_WAY_AREA_IMAGE_TYPE.TYPE1;
    case PATTERN_TYPE.P2A:
      return EATING_WAY_AREA_IMAGE_TYPE.TYPE2;
    case PATTERN_TYPE.P2B:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE3;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE4;
      }
    case PATTERN_TYPE.P3A:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE2;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      }
    case PATTERN_TYPE.P3B:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE4;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE4;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE4;
      }
    case PATTERN_TYPE.P4A:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE4;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE4;
      } else if (position === 3) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      }
    case PATTERN_TYPE.P4B:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      } else if (position === 3) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      }
    case PATTERN_TYPE.P5A:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE4;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else if (position === 3) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else if (position === 4) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      }
    case PATTERN_TYPE.P5B:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE2;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      } else if (position === 3) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      } else if (position === 4) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      }
    case PATTERN_TYPE.P6A:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else if (position === 3) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else if (position === 4) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else if (position === 5) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE6;
      }
    case PATTERN_TYPE.P6B:
      if (position === 1) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      } else if (position === 2) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE5;
      } else if (position === 3) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      } else if (position === 4) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      } else if (position === 5) {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      } else {
        return EATING_WAY_AREA_IMAGE_TYPE.TYPE7;
      }
    default:
      return EATING_WAY_AREA_IMAGE_TYPE.TYPE1;
  }
}
